/**
 * @fileoverview Upload a CSV of power bank units for a city. One row per
 * unit with an optional serial number; the validated rows are sent to
 * addPowerBankUnits. On success the parent asks the hold question.
 *
 * Modelled on the locations import (Locations/Modals/H2HLocationImport):
 * static backdrop while importing, errors grouped by row, buttons disabled
 * in flight.
 */
import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Button, CircularProgress } from '@material-ui/core'
import TextField from '@material-ui/core/TextField'
import MenuItem from '@material-ui/core/MenuItem'

import { Modal } from 'Components/Modal'
import { Dropzone } from 'Components/Form/Dropzone'
import CloseIcon from 'Assets/Images/icons/modalClose'
import { addPowerBankUnits } from 'Module/graphQL'
import { readGraphQLData } from 'Components/PowerBanks/hooks'
import { downloadStringAsFile, processCsvFile } from 'Utils/Misc'
import {
  MAX_POWER_BANK_UNITS_PER_UPLOAD,
  POWER_BANK_CSV_TEMPLATE,
  validatePowerBankCsv,
} from 'Utils/PowerBanks'
import { theme } from 'Assets/Styles/theme'

import { useTypeAndClientOptions } from '../../utils'

const Container = styled.div`
  padding: 20px;
  font-family: 'Proxima Nova', sans-serif;
  line-height: 1.6;
  display: flex;
  flex-direction: column;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Title = styled.div`
  font-size: 24px;
  font-weight: bold;
`

const BodyText = styled.p`
  font-size: 15px;
  color: #4a5568;
  margin: 16px 0 0 0;
`

const MainRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin: 16px 0;
`

const LeftSection = styled.div`
  flex: 2;
`

const RightSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`

const FileName = styled.div`
  font-size: 13px;
  color: #4a5568;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ErrorContainer = styled.div`
  max-height: 200px;
  overflow-y: auto;
`

const ErrorRow = styled.div`
  display: flex;
  gap: 10px;
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 10px;
`

const RowNumber = styled.div`
  min-width: 70px;
  font-weight: bold;
`

const ErrorMessage = styled.div`
  color: #c8102e;
  font-size: 14px;
`

const RequestError = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 4px;
  background: #fdecef;
  color: #c8102e;
  font-size: 14px;
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
`

const CancelButton = styled(Button)`
  && {
    border: 1px solid ${theme.colors.primary};
    color: ${theme.colors.primary};
    background-color: transparent;
    &:disabled {
      border-color: rgba(0, 0, 0, 0.12);
      color: rgba(0, 0, 0, 0.26);
    }
  }
`

const ImportButton = styled(Button)`
  && {
    background-color: ${theme.colors.primary};
    color: white;
    min-width: 140px;
    &:hover {
      background-color: ${theme.colors.primary};
      opacity: 0.9;
    }
    &:disabled {
      background-color: rgba(0, 0, 0, 0.12);
      color: rgba(0, 0, 0, 0.26);
    }
  }
`

const groupErrorsByRow = errors =>
  errors.reduce((acc, error) => {
    const row = error.split(':')[0]
    if (!acc[row]) acc[row] = []
    acc[row].push(error.replace(`${row}:`, '').trim())
    return acc
  }, {})

const downloadTemplate = () =>
  downloadStringAsFile(
    POWER_BANK_CSV_TEMPLATE,
    'power_banks_import_template.csv',
    'text/csv',
  )

const ImportPowerBanksModal = ({ cityId, handleCancel, handleConfirm }) => {
  const { clientOptions } = useTypeAndClientOptions()
  const clients = useMemo(
    () => clientOptions.filter(option => option.value !== null),
    [clientOptions],
  )

  const [clientId, setClientId] = useState('')
  const [file, setFile] = useState(null)
  const [validationErrors, setValidationErrors] = useState([])
  const [requestError, setRequestError] = useState(null)
  const [isImporting, setIsImporting] = useState(false)

  const onFileChange = useCallback(files => {
    setFile(files && files[0] ? files[0] : null)
    setValidationErrors([])
    setRequestError(null)
  }, [])

  const onImport = useCallback(async () => {
    if (!file || !clientId || isImporting) return
    setRequestError(null)
    setValidationErrors([])

    let csvData
    try {
      csvData = await processCsvFile(file)
    } catch (error) {
      setRequestError(
        `The file could not be read: ${error.message || 'unknown error'}`,
      )
      return
    }

    const validation = validatePowerBankCsv(csvData)
    if (!validation.isValid) {
      setValidationErrors(validation.errors)
      return
    }

    setIsImporting(true)
    try {
      const data = await readGraphQLData(
        await addPowerBankUnits({
          cityId,
          clientId,
          units: validation.units.map(unit => ({
            serialNumber: unit.serialNumber,
            comments: 'Power bank imported on the inventory page.',
          })),
        }),
      )
      const payload = data.addPowerBankUnits || {}
      handleConfirm({
        createdCount: payload.createdCount || 0,
        clientId,
      })
    } catch (error) {
      setRequestError(error.message || 'The power banks could not be imported.')
    } finally {
      setIsImporting(false)
    }
  }, [file, clientId, cityId, isImporting, handleConfirm])

  const groupedErrors = groupErrorsByRow(validationErrors)
  const canImport = Boolean(file && clientId) && !isImporting

  return (
    <Modal
      show
      onHide={isImporting ? () => {} : handleCancel}
      backdrop={isImporting ? 'static' : true}
      keyboard={!isImporting}
    >
      <Container>
        <Header>
          <Title>Import power banks</Title>
          {isImporting ? (
            <CircularProgress size={20} />
          ) : (
            <div style={{ cursor: 'pointer' }} onClick={handleCancel}>
              <CloseIcon />
            </div>
          )}
        </Header>
        <MainRow>
          <LeftSection>
            <TextField
              select
              fullWidth
              variant="outlined"
              margin="dense"
              label="Client"
              value={clientId}
              onChange={event => setClientId(event.target.value)}
              disabled={isImporting}
              helperText="The client the power banks belong to"
            >
              {clients.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <BodyText>
              One row per power bank. Put the serial printed on the unit in{' '}
              <strong>serialNumber</strong>. For units without a serial leave it
              empty and put how many in <strong>quantity</strong>. Up to{' '}
              {MAX_POWER_BANK_UNITS_PER_UPLOAD} power banks per file.
            </BodyText>
          </LeftSection>
          <RightSection>
            <Dropzone
              accept=".csv,text/csv"
              hint="Upload CSV"
              input={{ onChange: onFileChange }}
              meta={{ touched: false, error: null }}
              disabled={isImporting}
            />
            {file && <FileName title={file.name}>{file.name}</FileName>}
            <Button
              color="primary"
              onClick={downloadTemplate}
              disabled={isImporting}
              style={{ textTransform: 'none' }}
            >
              Download template
            </Button>
          </RightSection>
        </MainRow>

        {validationErrors.length > 0 && (
          <ErrorContainer>
            {Object.keys(groupedErrors).map(row => (
              <ErrorRow key={row}>
                <RowNumber>{row}</RowNumber>
                <div>
                  {groupedErrors[row].map(message => (
                    <ErrorMessage key={message}>{message}</ErrorMessage>
                  ))}
                </div>
              </ErrorRow>
            ))}
          </ErrorContainer>
        )}
        {requestError && (
          <RequestError role="alert">{requestError}</RequestError>
        )}

        <ButtonContainer>
          <CancelButton
            onClick={handleCancel}
            variant="outlined"
            disabled={isImporting}
          >
            Cancel
          </CancelButton>
          <ImportButton
            onClick={onImport}
            variant="contained"
            disabled={!canImport}
          >
            {isImporting ? 'Importing...' : 'Import'}
          </ImportButton>
        </ButtonContainer>
      </Container>
    </Modal>
  )
}

ImportPowerBanksModal.propTypes = {
  cityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleCancel: PropTypes.func.isRequired,
  /** Called with `{ createdCount, clientId }` once the API has created them. */
  handleConfirm: PropTypes.func.isRequired,
}

export default ImportPowerBanksModal
